import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Text,
  Spinner,
  MessageBar,
  MessageBarBody,
  Button,
  Badge,
  Input,
  SearchBox,
  makeStyles,
  tokens,
  mergeClasses,
} from '@fluentui/react-components';
import { ArrowClockwise20Regular, Add20Regular, DismissRegular, Save20Regular } from '@fluentui/react-icons';
import type { AuthArgs } from '../../api/authArgs';
import { getConfigDocument, saveConfigDocument, ConfigConcurrencyError } from '../../api/configStoreClient';

const useStyles = makeStyles({
  // Fill the editor pane's fixed-height flex column so only the table body scrolls (no double scrollbar).
  root: { display: 'flex', flexDirection: 'column', minHeight: 0, flex: 1, gap: tokens.spacingVerticalS },
  header: { display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalS, flexWrap: 'wrap', flexShrink: 0 },
  spacer: { flex: 1 },
  // Grow to consume the remaining height and scroll internally; flexShrink lets it shrink on short windows.
  tableWrap: { overflow: 'auto', border: `1px solid ${tokens.colorNeutralStroke1}`, borderRadius: tokens.borderRadiusMedium, flex: 1, minHeight: '120px' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: tokens.fontSizeBase300 },
  th: {
    textAlign: 'left',
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalM}`,
    borderBottom: `1px solid ${tokens.colorNeutralStroke1}`,
    background: tokens.colorNeutralBackground2,
    position: 'sticky',
    top: 0,
    zIndex: 1,
    whiteSpace: 'nowrap',
    color: tokens.colorNeutralForeground2,
    fontWeight: tokens.fontWeightSemibold,
  },
  thEnv: { textAlign: 'center' },
  td: { padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalM}`, borderBottom: `1px solid ${tokens.colorNeutralStroke2}`, verticalAlign: 'middle' },
  tdCenter: { textAlign: 'center' },
  colEditable: { background: tokens.colorNeutralBackground1 },
  colReadonly: { background: tokens.colorNeutralBackground2 },
  flagName: { fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace', color: tokens.colorNeutralForeground1 },
  cell: { display: 'inline-flex', alignItems: 'center', gap: tokens.spacingHorizontalXS, justifyContent: 'center' },
  // Custom pill toggle (replaces Fluent Switch, whose checked thumb renders invisibly under this app's theme).
  toggle: {
    position: 'relative',
    width: '38px',
    height: '20px',
    borderRadius: '999px',
    border: 'none',
    padding: 0,
    background: tokens.colorNeutralStroke1,
    cursor: 'pointer',
    flexShrink: 0,
    transition: 'background 0.15s ease',
    display: 'inline-block',
    verticalAlign: 'middle',
  },
  toggleOn: { background: tokens.colorBrandBackground },
  toggleReadonly: { cursor: 'default' },
  toggleReadonlyOn: { background: tokens.colorBrandBackground2 },
  toggleReadonlyOff: { background: tokens.colorNeutralBackground5 },
  thumb: {
    position: 'absolute',
    top: '2px',
    left: '2px',
    width: '16px',
    height: '16px',
    borderRadius: '50%',
    background: tokens.colorNeutralForegroundOnBrand,
    boxShadow: tokens.shadow2,
    transition: 'transform 0.15s ease',
  },
  thumbOn: { transform: 'translateX(18px)' },
  clearBtn: { minWidth: 'auto' },
  addRow: { display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalS, flexWrap: 'wrap', flexShrink: 0 },
});

type LayerFlags = {
  env: string | null; // null = Base
  etag?: string;
  exists: boolean;
  // Full parsed document, so non-boolean keys (typed values) are preserved verbatim on save.
  raw: Record<string, unknown>;
};

const BASE = '__base__';
const layerKey = (env: string | null) => env ?? BASE;

function parseDoc(json: string | null | undefined): Record<string, unknown> {
  if (!json) return {};
  try {
    const v = JSON.parse(json);
    return v && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : {};
  } catch {
    return {};
  }
}

const isBool = (v: unknown): v is boolean => typeof v === 'boolean';

/** Small custom on/off toggle. Interactive when onToggle is set; otherwise a crisp read-only indicator. */
function PillToggle(props: { checked: boolean; readOnly?: boolean; disabled?: boolean; label: string; title?: string; onToggle?: (next: boolean) => void }) {
  const styles = useStyles();
  const { checked, readOnly, disabled, label, title, onToggle } = props;
  const trackClass = readOnly
    ? mergeClasses(styles.toggle, styles.toggleReadonly, checked ? styles.toggleReadonlyOn : styles.toggleReadonlyOff)
    : mergeClasses(styles.toggle, checked && styles.toggleOn);
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      aria-readonly={readOnly || undefined}
      title={title}
      disabled={disabled}
      className={trackClass}
      onClick={readOnly || !onToggle ? undefined : () => onToggle(!checked)}
    >
      <span className={mergeClasses(styles.thumb, checked && styles.thumbOn)} />
    </button>
  );
}

export interface FeatureFlagTogglesProps {
  getAuthArgs: () => Promise<AuthArgs | null>;
  group: string;
  collection: string;
  keyName: string;
  /** Selectable environments from the store (includes "Default" which maps to Base). */
  overrideEnvs: string[];
  /** The environment this API instance runs in (author/int/ppe/prod/local). */
  runningEnv: string;
  /** True when running locally (unshared sandbox — every override column editable). */
  isLocalEnv: boolean;
  /** Called after a new override layer is created so the tree can refresh. */
  onTreeChanged?: () => void;
}

/**
 * Unleash-style feature-flag toggle table: one row per boolean flag, one column per environment layer
 * (Base + each override). Editing follows the same per-environment restriction as the rest of the config
 * store, tightened for flags: the Base layer is READ-ONLY here (it is the shipped source of truth — change it
 * in config-store/feature-flags/… and deploy), and only this stamp's OWN environment column is editable
 * (a developer's local environment maps to Author). Each editable cell is a plain On/Off toggle that defaults
 * to the Base value until it is set; a × appears next to a cell ONLY while it has an unsaved change and reverts
 * just that cell to its last-saved value. Edits are batched into a working draft and persisted together by the
 * Save button (one write for the layer, optimistic If-Match ETag). The stored env document remains a sparse
 * delta — only flags actually set for that environment are written; the rest follow Base. Non-boolean keys are
 * preserved verbatim and not shown as toggles.
 */
export function FeatureFlagToggles(props: FeatureFlagTogglesProps) {
  const { getAuthArgs, group, collection, keyName, overrideEnvs, runningEnv, isLocalEnv, onTreeChanged } = props;
  const styles = useStyles();

  const [layers, setLayers] = useState<Record<string, LayerFlags>>({});
  // Working copy of the single editable env layer's flag map. Toggles/clears/adds mutate this only; nothing is
  // written to the store until Save. Mirrors layers[effectiveEnv].raw after a load/save.
  const [draft, setDraft] = useState<Record<string, unknown>>({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [newFlag, setNewFlag] = useState('');
  const [search, setSearch] = useState('');

  // Environment columns: Base first, then each configured override env ("Default" maps to Base and is dropped).
  const envColumns = useMemo(
    () => overrideEnvs.map((e) => e.trim()).filter((e) => e && e.toLowerCase() !== 'default').map((e) => e.toLowerCase()),
    [overrideEnvs],
  );
  const columns: (string | null)[] = useMemo(() => [null, ...envColumns], [envColumns]);

  // A developer's local environment IS the Author environment, so map local/empty to "author". The editable
  // column is always exactly this stamp's own environment; Base and every other environment are read-only
  // (Base is the shipped source of truth — edit the files and deploy).
  const effectiveEnv = useMemo(
    () => (isLocalEnv ? 'author' : runningEnv).trim().toLowerCase(),
    [isLocalEnv, runningEnv],
  );
  const canToggle = useCallback(
    (env: string | null): boolean => env !== null && env.toLowerCase() === effectiveEnv,
    [effectiveEnv],
  );

  const editableLayerKey = layerKey(effectiveEnv);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    setNotice(null);
    try {
      const auth = await getAuthArgs();
      if (!auth) {
        setError('You must be signed in to manage feature flags.');
        return;
      }
      const envs: (string | null)[] = [null, ...envColumns];
      const docs = await Promise.all(envs.map((env) => getConfigDocument(auth, group, collection, keyName, env)));
      const built: Record<string, LayerFlags> = {};
      envs.forEach((env, i) => {
        const doc = docs[i];
        built[layerKey(env)] = { env, etag: doc?.etag ?? undefined, exists: doc !== null, raw: parseDoc(doc?.json) };
      });
      setLayers(built);
      // Reset the working draft to the freshly loaded editable layer (a load is an explicit refresh).
      setDraft({ ...(built[editableLayerKey]?.raw ?? {}) });
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, [getAuthArgs, group, collection, keyName, envColumns, editableLayerKey]);

  useEffect(() => { void load(); }, [load]);

  // Union of flag names that are boolean somewhere (server layers + the working draft), sorted stably.
  const flagNames = useMemo(() => {
    const names = new Set<string>();
    for (const lk of Object.keys(layers)) {
      for (const [k, v] of Object.entries(layers[lk].raw)) {
        if (isBool(v)) names.add(k);
      }
    }
    for (const [k, v] of Object.entries(draft)) {
      if (isBool(v)) names.add(k);
    }
    return [...names].sort((a, b) => a.localeCompare(b));
  }, [layers, draft]);

  // Case-insensitive substring filter over the flag name.
  const visibleFlagNames = useMemo(() => {
    const q = search.trim().toLowerCase();
    return q ? flagNames.filter((f) => f.toLowerCase().includes(q)) : flagNames;
  }, [flagNames, search]);

  const baseValue = useCallback(
    (flag: string): boolean => {
      const v = layers[BASE]?.raw[flag];
      return isBool(v) ? v : false;
    },
    [layers],
  );

  // Effective value of a flag for a layer map: the map's own value, else the Base value, else undefined
  // (the flag is in neither this layer nor Base — a brand-new key). Dirty state is judged on the EFFECTIVE
  // value, not raw key presence, so toggling an inherited cell back to the Base value is NOT a change: a
  // draft key equal to Base that the saved layer didn't have is equivalent to inheriting.
  const effectiveOf = useCallback(
    (map: Record<string, unknown>, flag: string): boolean | undefined => {
      if (isBool(map[flag])) return map[flag] as boolean;
      const b = layers[BASE]?.raw[flag];
      return isBool(b) ? b : undefined;
    },
    [layers],
  );

  const savedRaw = layers[editableLayerKey]?.raw ?? {};
  const isCellDirty = useCallback(
    (flag: string): boolean => effectiveOf(draft, flag) !== effectiveOf(savedRaw, flag),
    [effectiveOf, draft, savedRaw],
  );

  // Unsaved edits exist when any flag's effective value in the draft differs from the saved layer.
  const dirty = useMemo(() => flagNames.some(isCellDirty), [flagNames, isCellDirty]);

  // Local edits (no network). Toggling sets the flag's value for this env in the draft. All edits are persisted
  // together by Save.
  const setDraftFlag = useCallback((flag: string, value: boolean) => {
    setDraft((d) => ({ ...d, [flag]: value }));
    setNotice(null);
  }, []);

  // Revert one cell's UNSAVED change back to its last-saved state: restore the saved value if the layer had one,
  // otherwise drop the key so the cell follows Base again. (This is the × next to a changed cell.)
  const revertCell = useCallback((flag: string) => {
    setDraft((d) => {
      const saved = layers[editableLayerKey]?.raw ?? {};
      const next = { ...d };
      if (Object.prototype.hasOwnProperty.call(saved, flag)) {
        next[flag] = saved[flag];
      } else {
        delete next[flag];
      }
      return next;
    });
    setNotice(null);
  }, [layers, editableLayerKey]);

  const canAddFlag = isLocalEnv;
  const addFlag = useCallback(() => {
    const name = newFlag.trim();
    if (!name) return;
    if (flagNames.includes(name)) {
      setError(`Flag "${name}" already exists.`);
      return;
    }
    setNewFlag('');
    setDraftFlag(name, false);
  }, [newFlag, flagNames, setDraftFlag]);

  const discard = useCallback(() => {
    setDraft({ ...(layers[editableLayerKey]?.raw ?? {}) });
    setError(null);
    setNotice(null);
  }, [layers, editableLayerKey]);

  // Persist the whole draft as the editable env layer's document (one write, optimistic ETag).
  const save = useCallback(async () => {
    const layer = layers[editableLayerKey];
    setSaving(true);
    setError(null);
    setNotice(null);
    try {
      const auth = await getAuthArgs();
      if (!auth) {
        setError('You must be signed in to save.');
        return;
      }
      // Keep the stored env document a sparse delta: drop any flag this session added whose value equals Base
      // (a redundant explicit-equals-Base key from toggling an inherited cell). Flags that were already
      // explicit in the saved doc are left untouched, so existing pins are never silently dropped.
      const savedNow = layer?.raw ?? {};
      const toSave: Record<string, unknown> = {};
      for (const [k, v] of Object.entries(draft)) {
        const wasExplicit = Object.prototype.hasOwnProperty.call(savedNow, k);
        const baseV = layers[BASE]?.raw[k];
        const equalsBase = isBool(v) && isBool(baseV) && v === baseV;
        if (!wasExplicit && equalsBase) continue;
        toSave[k] = v;
      }
      const wasNew = !layer?.exists;
      const saved = await saveConfigDocument(auth, {
        group,
        collection,
        key: keyName,
        environment: effectiveEnv,
        json: JSON.stringify(toSave, null, 2),
        etag: layer?.etag,
      });
      setLayers((m) => ({
        ...m,
        [editableLayerKey]: { env: effectiveEnv, etag: saved.etag ?? undefined, exists: true, raw: parseDoc(saved.json) },
      }));
      setDraft(parseDoc(saved.json));
      setNotice('Saved.');
      if (wasNew) onTreeChanged?.();
    } catch (err) {
      if (err instanceof ConfigConcurrencyError) {
        // Someone else changed this layer. Refresh its ETag (keeping the user's draft) so a re-Save applies
        // against the latest version — the draft is not discarded.
        try {
          const auth = await getAuthArgs();
          if (auth) {
            const fresh = await getConfigDocument(auth, group, collection, keyName, effectiveEnv);
            setLayers((m) => ({
              ...m,
              [editableLayerKey]: { env: effectiveEnv, etag: fresh?.etag ?? undefined, exists: fresh !== null, raw: parseDoc(fresh?.json) },
            }));
          }
        } catch { /* ignore refresh failure; the error below is the actionable message */ }
        setError(`${effectiveEnv}: this layer was changed by someone else. Review and click Save again to reapply your changes.`);
      } else {
        setError(err instanceof Error ? err.message : String(err));
      }
    } finally {
      setSaving(false);
    }
  }, [layers, editableLayerKey, getAuthArgs, group, collection, keyName, effectiveEnv, draft, onTreeChanged]);

  if (loading && Object.keys(layers).length === 0) {
    return <Spinner label="Loading feature flags…" />;
  }

  const renderCell = (flag: string, env: string | null) => {
    const editable = canToggle(env);

    if (!editable) {
      // Read-only column (Base, or another stamp's environment): a non-interactive toggle showing the effective
      // value (the layer's own value, or Base when the layer doesn't set this flag).
      const layer = layers[layerKey(env)];
      const hasOwn = env === null ? true : !!layer && isBool(layer.raw[flag]);
      const value = hasOwn && layer ? (layer.raw[flag] as boolean) : baseValue(flag);
      return (
        <span className={styles.cell}>
          <PillToggle checked={value} readOnly label={`${flag} in ${env ?? 'Base'} (read-only)`} title={`Read-only in ${env ?? 'Base'}`} />
        </span>
      );
    }

    // Editable env column: a plain On/Off toggle. The value defaults to Base until you set it. The × appears
    // ONLY when this cell has an unsaved change, and reverts just this cell to its last-saved value. Nothing is
    // written to the store until Save.
    const inDraft = Object.prototype.hasOwnProperty.call(draft, flag);
    const value = inDraft ? (draft[flag] as boolean) : baseValue(flag);
    const cellDirty = isCellDirty(flag);
    return (
      <span className={styles.cell}>
        <PillToggle
          checked={value}
          disabled={saving}
          label={`${flag} in ${env}`}
          title={`Toggle ${flag} for ${env}`}
          onToggle={(next) => setDraftFlag(flag, next)}
        />
        {cellDirty && (
          <Button
            appearance="subtle"
            size="small"
            className={styles.clearBtn}
            icon={<DismissRegular />}
            disabled={saving}
            onClick={() => revertCell(flag)}
            title="Discard this unsaved change"
            aria-label={`Discard the unsaved change to ${flag} in ${env}`}
          />
        )}
      </span>
    );
  };

  return (
    <div className={styles.root}>
      <div className={styles.header}>
        <Text weight="semibold" size={400}>
          Feature flags ({search.trim() ? `${visibleFlagNames.length} of ${flagNames.length}` : flagNames.length})
        </Text>
        {dirty && <Badge appearance="tint" color="warning">Unsaved changes</Badge>}
        <SearchBox
          placeholder="Search flags…"
          value={search}
          onChange={(_, d) => setSearch(d.value)}
          style={{ minWidth: 220, marginLeft: tokens.spacingHorizontalM }}
          aria-label="Search feature flags by name"
        />
        <div className={styles.spacer} />
        <Button appearance="subtle" icon={<ArrowClockwise20Regular />} onClick={() => void load()} disabled={loading || saving}>
          Reload
        </Button>
        <Button appearance="subtle" onClick={discard} disabled={!dirty || saving}>
          Discard
        </Button>
        <Button appearance="primary" icon={<Save20Regular />} onClick={() => void save()} disabled={!dirty || saving}>
          Save
        </Button>
      </div>

      {error && (
        <MessageBar intent="error">
          <MessageBarBody>{error}</MessageBarBody>
        </MessageBar>
      )}
      {notice && (
        <MessageBar intent="success">
          <MessageBarBody>{notice}</MessageBarBody>
        </MessageBar>
      )}

      <div className={styles.tableWrap}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th className={styles.th}>Feature flag</th>
              {columns.map((env) => (
                <th key={layerKey(env)} className={`${styles.th} ${styles.thEnv} ${canToggle(env) ? styles.colEditable : styles.colReadonly}`}>
                  {env === null ? 'Base' : env}
                  {canToggle(env)
                    ? <Badge appearance="tint" color="success" size="small" style={{ marginLeft: 6 }}>editable</Badge>
                    : <Badge appearance="tint" color="informative" size="small" style={{ marginLeft: 6 }}>read-only</Badge>}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {flagNames.length === 0 ? (
              <tr>
                <td className={styles.td} colSpan={columns.length + 1}>
                  <Text italic>No feature flags yet.{canAddFlag ? ' Add one below.' : ''}</Text>
                </td>
              </tr>
            ) : visibleFlagNames.length === 0 ? (
              <tr>
                <td className={styles.td} colSpan={columns.length + 1}>
                  <Text italic>No flags match &ldquo;{search.trim()}&rdquo;.</Text>
                </td>
              </tr>
            ) : (
              visibleFlagNames.map((flag) => (
                <tr key={flag}>
                  <td className={`${styles.td} ${styles.flagName}`}>{flag}</td>
                  {columns.map((env) => (
                    <td key={layerKey(env)} className={`${styles.td} ${styles.tdCenter} ${canToggle(env) ? styles.colEditable : styles.colReadonly}`}>
                      {renderCell(flag, env)}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {canAddFlag && (
        <div className={styles.addRow}>
          <Input
            size="small"
            placeholder="new.flag.name.enabled"
            value={newFlag}
            onChange={(_, d) => setNewFlag(d.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') addFlag(); }}
            style={{ minWidth: 280 }}
            disabled={saving}
          />
          <Button size="small" appearance="primary" icon={<Add20Regular />} onClick={addFlag} disabled={saving || !newFlag.trim()}>
            Add feature flag
          </Button>
        </div>
      )}
    </div>
  );
}
